import type { AuthFlowController, AuthFlowState } from "./auth-flow.ts";
import type { AuthGateway } from "./auth-types.ts";
import { TOTP_ISSUER, validateTotpEnrollmentUri } from "./totp-qr.ts";

const STAGING_PROJECT_ID = "tu-conferences-stg-2026";
const STAGING_ORIGIN = "https://tu-conferences-stg-2026.web.app";
const SYNTHETIC_EMAIL = /^phase12-viewer-[a-f0-9]{32}@example\.invalid$/u;
const SAFE_UID = /^[A-Za-z0-9_.-]{1,128}$/u;
const BASE32 = /^[A-Z2-7]+={0,6}$/u;
const HOOK_NAME = "tu.phase12.synthetic-viewer.v1";
const SOURCE_DIGEST = /^sha256:[a-f0-9]{64}$/;
const QR_WAIT_MILLISECONDS = 5_000;

export const PHASE12_SYNTHETIC_VIEWER_HOOK = Symbol.for(HOOK_NAME);

export interface Phase12SyntheticViewerInput {
  readonly email: string;
  readonly initialPassword: string;
  readonly expectedUid: string;
}

export interface Phase12SyntheticViewerReceipt {
  readonly firstPasswordAccepted: boolean;
  readonly totpQrRendered: boolean;
  readonly totpQrIssuerVerified: boolean;
  readonly totpQrAccountVerified: boolean;
  readonly totpEnrollmentCompleted: boolean;
  readonly signedOutAfterEnrollment: boolean;
  readonly freshPasswordAccepted: boolean;
  readonly freshTotpChallengeRequired: boolean;
  readonly freshTotpAccepted: boolean;
  readonly viewerIdToken: string;
}

interface Phase12HookEnvironment {
  readonly projectId: string;
  readonly origin: string;
}

interface Phase12HookDependencies {
  readonly controller: AuthFlowController;
  readonly gateway: AuthGateway;
  readonly environment: Phase12HookEnvironment;
  readonly now?: () => number;
  readonly waitForQrRendered?: () => Promise<boolean>;
}

type Phase12SyntheticViewerHook = (
  input: Phase12SyntheticViewerInput,
) => Promise<Phase12SyntheticViewerReceipt>;

interface Phase12LiveCertificationHook {
  readonly sourceDigest: string;
  attestFirebaseRuntime(input: unknown): Promise<{
    readonly firebaseRuntimeConfigVerified: boolean;
  }>;
  certifySyntheticViewer: Phase12SyntheticViewerHook;
  attestAdminGoogleChallenge(): Promise<{ challengeReady: true }>;
  submitAdminTotp(code: string): Promise<
    | { readonly adminIdToken: string }
    | {
        readonly adminIdToken: null;
        readonly failureCategory:
          | "verification_rejected"
          | "session_attestation_failed"
          | "token_unavailable";
      }
  >;
  signOut(): Promise<{ signedOut: true }>;
}

const FIREBASE_RUNTIME_ATTESTATION_KEYS = Object.freeze([
  "apiKey",
  "appId",
  "authDomain",
  "messagingSenderId",
  "projectId",
  "storageBucket",
] as const);

type FirebaseRuntimeAttestationConfig = Readonly<
  Record<(typeof FIREBASE_RUNTIME_ATTESTATION_KEYS)[number], string>
>;

function firebaseRuntimeMatches(
  rawInput: unknown,
  runtimeConfig: FirebaseRuntimeAttestationConfig,
): boolean {
  if (!rawInput || typeof rawInput !== "object" || Array.isArray(rawInput)) {
    return false;
  }
  const input = rawInput as Record<string, unknown>;
  const keys = Object.keys(input).sort();
  if (
    keys.length !== FIREBASE_RUNTIME_ATTESTATION_KEYS.length ||
    keys.some((key, index) => key !== FIREBASE_RUNTIME_ATTESTATION_KEYS[index])
  ) {
    return false;
  }
  return FIREBASE_RUNTIME_ATTESTATION_KEYS.every(
    (key) =>
      typeof input[key] === "string" && input[key] === runtimeConfig[key],
  );
}

declare global {
  interface Window {
    [PHASE12_SYNTHETIC_VIEWER_HOOK]?: Phase12LiveCertificationHook;
  }
}

export class Phase12SyntheticViewerHookError extends Error {
  readonly reasonCode: string;

  constructor(reasonCode: string) {
    super(reasonCode);
    this.name = "Phase12SyntheticViewerHookError";
    this.reasonCode = reasonCode;
  }
}

function fail(reasonCode: string): never {
  throw new Phase12SyntheticViewerHookError(reasonCode);
}

function exactInput(value: unknown): Phase12SyntheticViewerInput {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    fail("phase12_synthetic_viewer_input_invalid");
  }
  const input = value as Record<string, unknown>;
  if (
    Object.keys(input).sort().join("|") !==
      "email|expectedUid|initialPassword" ||
    typeof input.email !== "string" ||
    !SYNTHETIC_EMAIL.test(input.email) ||
    typeof input.initialPassword !== "string" ||
    input.initialPassword.length < 16 ||
    input.initialPassword.length > 256 ||
    typeof input.expectedUid !== "string" ||
    !SAFE_UID.test(input.expectedUid)
  ) {
    fail("phase12_synthetic_viewer_input_invalid");
  }
  return input as unknown as Phase12SyntheticViewerInput;
}

function assertStaging(environment: Phase12HookEnvironment) {
  if (
    environment.projectId !== STAGING_PROJECT_ID ||
    environment.origin !== STAGING_ORIGIN
  ) {
    fail("phase12_synthetic_viewer_production_forbidden");
  }
}

function decodeBase32(secret: string): Uint8Array {
  if (!BASE32.test(secret) || secret.length > 134) {
    fail("phase12_synthetic_viewer_totp_secret_invalid");
  }
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  const unpadded = secret.replace(/=+$/u, "");
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const character of unpadded) {
    const index = alphabet.indexOf(character);
    if (index < 0) fail("phase12_synthetic_viewer_totp_secret_invalid");
    value = (value << 5) | index;
    bits += 5;
    if (bits >= 8) {
      bytes.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  if (bytes.length < 10 || bytes.length > 80) {
    fail("phase12_synthetic_viewer_totp_secret_invalid");
  }
  return Uint8Array.from(bytes);
}

export async function generatePhase12Totp(
  secret: string,
  timestampMilliseconds: number,
): Promise<string> {
  if (!Number.isFinite(timestampMilliseconds) || timestampMilliseconds < 0) {
    fail("phase12_synthetic_viewer_totp_time_invalid");
  }
  const keyBytes = decodeBase32(secret);
  const keyMaterial = keyBytes.buffer.slice(
    keyBytes.byteOffset,
    keyBytes.byteOffset + keyBytes.byteLength,
  ) as ArrayBuffer;
  const counter = Math.floor(timestampMilliseconds / 30_000);
  const counterBytes = new Uint8Array(8);
  new DataView(counterBytes.buffer).setBigUint64(0, BigInt(counter));
  try {
    const key = await crypto.subtle.importKey(
      "raw",
      keyMaterial,
      { name: "HMAC", hash: "SHA-1" },
      false,
      ["sign"],
    );
    const digest = new Uint8Array(
      await crypto.subtle.sign("HMAC", key, counterBytes),
    );
    const finalByte = digest.at(-1);
    if (finalByte === undefined) {
      fail("phase12_synthetic_viewer_totp_generation_failed");
    }
    const offset = finalByte & 0x0f;
    const first = digest[offset];
    const second = digest[offset + 1];
    const third = digest[offset + 2];
    const fourth = digest[offset + 3];
    if (
      first === undefined ||
      second === undefined ||
      third === undefined ||
      fourth === undefined
    ) {
      fail("phase12_synthetic_viewer_totp_generation_failed");
    }
    const binary =
      ((first & 0x7f) << 24) |
      ((second & 0xff) << 16) |
      ((third & 0xff) << 8) |
      (fourth & 0xff);
    return String(binary % 1_000_000).padStart(6, "0");
  } finally {
    keyBytes.fill(0);
    new Uint8Array(keyMaterial).fill(0);
    counterBytes.fill(0);
  }
}

async function defaultWaitForQrRendered(): Promise<boolean> {
  const deadline = Date.now() + QR_WAIT_MILLISECONDS;
  while (Date.now() < deadline) {
    const image = document.querySelector(".mfa-qr-frame img");
    if (image instanceof HTMLImageElement && image.width > 0) return true;
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );
  }
  return false;
}

function enrollmentState(state: AuthFlowState) {
  if (state.view !== "mfa-enrollment") {
    fail("phase12_synthetic_viewer_enrollment_not_reached");
  }
  return state;
}

export function createPhase12SyntheticViewerHook({
  controller,
  gateway,
  environment,
  now = Date.now,
  waitForQrRendered = defaultWaitForQrRendered,
}: Phase12HookDependencies): Phase12SyntheticViewerHook {
  assertStaging(environment);
  let used = false;

  return async (rawInput) => {
    if (used) fail("phase12_synthetic_viewer_hook_already_used");
    used = true;
    const input = exactInput(rawInput);
    let password: string | null = input.initialPassword;
    let secretKey: string | null = null;
    let token: string | null = null;
    try {
      await controller.signOut();
      await controller.signInWithPassword(input.email, password);
      const enrollment = enrollmentState(controller.getSnapshot());
      const metadata = validateTotpEnrollmentUri(
        enrollment.setupUrl,
        enrollment.accountLabel,
      );
      if (
        metadata.issuer !== TOTP_ISSUER ||
        metadata.accountLabel !== input.email
      ) {
        fail("phase12_synthetic_viewer_totp_metadata_invalid");
      }
      secretKey = enrollment.secretKey;
      const qrRendered = await waitForQrRendered();
      if (!qrRendered) fail("phase12_synthetic_viewer_qr_not_rendered");

      const enrollmentCode = await generatePhase12Totp(secretKey, now());
      await controller.enrollMfa(enrollmentCode);
      if (controller.getSnapshot().view !== "mfa-enrollment-success") {
        fail("phase12_synthetic_viewer_enrollment_failed");
      }
      controller.finishEnrollment();
      if (controller.getSnapshot().view !== "sign-in") {
        fail("phase12_synthetic_viewer_enrollment_failed");
      }

      await controller.signInWithPassword(input.email, password);
      if (controller.getSnapshot().view !== "mfa-challenge") {
        fail("phase12_synthetic_viewer_fresh_challenge_missing");
      }
      password = null;
      const challengeCode = await generatePhase12Totp(secretKey, now());
      await controller.verifyMfa(challengeCode);
      const finalState = controller.getSnapshot();
      if (
        finalState.view !== "authenticated" ||
        finalState.session.uid !== input.expectedUid ||
        finalState.session.role !== "viewer" ||
        finalState.session.provider !== "password" ||
        finalState.session.secondFactor !== "totp"
      ) {
        fail("phase12_synthetic_viewer_session_invalid");
      }
      token = await gateway.getAccessToken(true);
      if (typeof token !== "string" || token.length < 24) {
        fail("phase12_synthetic_viewer_token_invalid");
      }
      await controller.signOut();
      return Object.freeze({
        firstPasswordAccepted: true,
        totpQrRendered: true,
        totpQrIssuerVerified: true,
        totpQrAccountVerified: true,
        totpEnrollmentCompleted: true,
        signedOutAfterEnrollment: true,
        freshPasswordAccepted: true,
        freshTotpChallengeRequired: true,
        freshTotpAccepted: true,
        viewerIdToken: token,
      });
    } finally {
      if (controller.getSnapshot().view !== "sign-in") {
        try {
          await controller.signOut();
        } catch {
          // Cleanup failure is surfaced later by the live driver residual check.
        }
      }
      password = null;
      secretKey = null;
      token = null;
    }
  };
}

export function installPhase12SyntheticViewerHook(
  controller: AuthFlowController,
  gateway: AuthGateway,
  projectId: string,
  sourceDigest: string | null,
  firebaseRuntimeConfig: FirebaseRuntimeAttestationConfig,
): boolean {
  const environment = {
    projectId,
    origin: window.location.origin,
  };
  if (
    environment.projectId !== STAGING_PROJECT_ID ||
    environment.origin !== STAGING_ORIGIN ||
    typeof sourceDigest !== "string" ||
    !SOURCE_DIGEST.test(sourceDigest)
  ) {
    return false;
  }
  const hook = createPhase12SyntheticViewerHook({
    controller,
    gateway,
    environment,
  });
  let adminChallengeReady = false;
  let adminCompleted = false;
  Object.defineProperty(window, PHASE12_SYNTHETIC_VIEWER_HOOK, {
    configurable: true,
    enumerable: false,
    writable: false,
    value: Object.freeze({
      sourceDigest,
      async attestFirebaseRuntime(input: unknown) {
        return Object.freeze({
          firebaseRuntimeConfigVerified: firebaseRuntimeMatches(
            input,
            firebaseRuntimeConfig,
          ),
        });
      },
      certifySyntheticViewer: hook,
      async attestAdminGoogleChallenge() {
        if (adminChallengeReady || adminCompleted) {
          fail("phase12_admin_hook_already_used");
        }
        if (controller.getSnapshot().view !== "mfa-challenge") {
          fail("phase12_admin_totp_challenge_missing");
        }
        adminChallengeReady = true;
        return Object.freeze({ challengeReady: true as const });
      },
      async submitAdminTotp(code: string) {
        if (
          !adminChallengeReady ||
          adminCompleted ||
          !/^[0-9]{6}$/u.test(code)
        ) {
          code = "";
          fail("phase12_admin_totp_submission_invalid");
        }
        try {
          await controller.verifyMfa(code);
        } finally {
          code = "";
        }
        const state = controller.getSnapshot();
        if (state.view === "mfa-challenge") {
          return Object.freeze({
            adminIdToken: null,
            failureCategory: "verification_rejected" as const,
          });
        }
        if (
          state.view !== "authenticated" ||
          state.session.role !== "admin" ||
          state.session.provider !== "google.com" ||
          state.session.secondFactor !== "totp"
        ) {
          return Object.freeze({
            adminIdToken: null,
            failureCategory: "session_attestation_failed" as const,
          });
        }
        let adminIdToken: string;
        try {
          adminIdToken = await gateway.getAccessToken(true);
        } catch {
          return Object.freeze({
            adminIdToken: null,
            failureCategory: "token_unavailable" as const,
          });
        }
        adminChallengeReady = false;
        adminCompleted = true;
        return Object.freeze({ adminIdToken });
      },
      async signOut() {
        await controller.signOut();
        return Object.freeze({ signedOut: true as const });
      },
    }),
  });
  return true;
}
